import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export interface TooltipProps {
  /** Tooltip content */
  content: React.ReactNode
  /** Trigger element */
  children: React.ReactElement
  /** Tooltip placement */
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** Delay before showing (ms) */
  delay?: number
  /** Tooltip variant */
  variant?: 'default' | 'light'
  /** Additional class name */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  delay = 200,
  variant = 'default',
  className,
  'data-id': dataId,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const offset = 8

    let top = 0
    let left = 0

    switch (placement) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - offset
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
        break
      case 'bottom':
        top = triggerRect.bottom + offset
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
        break
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
        left = triggerRect.left - tooltipRect.width - offset
        break
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
        left = triggerRect.right + offset
        break
    }

    // Keep tooltip within viewport
    const padding = 8
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding))
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding))

    setPosition({ top, left })
  }, [placement])

  useEffect(() => {
    if (isVisible) {
      calculatePosition()
      window.addEventListener('scroll', calculatePosition, true)
      window.addEventListener('resize', calculatePosition)
      return () => {
        window.removeEventListener('scroll', calculatePosition, true)
        window.removeEventListener('resize', calculatePosition)
      }
    }
  }, [isVisible, calculatePosition])

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
  }

  const variantStyles = {
    default: 'bg-primary text-primary-foreground',
    light: 'bg-background text-foreground border border-border shadow-lg',
  }

  // Get original props from child - use type assertion for safety
  const childProps = children.props as Record<string, unknown>

  const clonedChild = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      triggerRef.current = e.currentTarget as HTMLElement
      showTooltip()
      if (typeof childProps.onMouseEnter === 'function') {
        childProps.onMouseEnter(e)
      }
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hideTooltip()
      if (typeof childProps.onMouseLeave === 'function') {
        childProps.onMouseLeave(e)
      }
    },
    onFocus: (e: React.FocusEvent) => {
      triggerRef.current = e.currentTarget as HTMLElement
      showTooltip()
      if (typeof childProps.onFocus === 'function') {
        childProps.onFocus(e)
      }
    },
    onBlur: (e: React.FocusEvent) => {
      hideTooltip()
      if (typeof childProps.onBlur === 'function') {
        childProps.onBlur(e)
      }
    },
  } as React.Attributes)

  return (
    <>
      {clonedChild}
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={cn(
              'fixed z-50 px-3 py-2 text-[12px] rounded-lg max-w-[200px] pointer-events-none',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              variantStyles[variant],
              className
            )}
            style={{ top: position.top, left: position.left }}
            data-id={dataId}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}
